import { useState, useMemo } from "react";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, ReferenceLine, Cell,
} from "recharts";
import TransactionRow from "../shared/TransactionRow";
import {
  TRANSACTIONS, RECURRING_TRANSACTIONS,
  MONTHLY_SPENDING_TREND, PROJECTED_MONTHS, fmt,
} from "../../data/mockData";
import { useIsMobile } from "../../hooks/useIsMobile";

// ─── Constants ────────────────────────────────────────────────────────────────
const TABS = ["Transactions", "Recurring", "Insights"];

const CAT_META = {
  groceries:     { label: "Groceries",     color: "#52D68A", icon: "🛒" },
  dining:        { label: "Dining",         color: "#F4B8B8", icon: "🍽️" },
  transport:     { label: "Transport",      color: "#B8D4E8", icon: "🚗" },
  subscriptions: { label: "Subscriptions",  color: "#D4B8E8", icon: "📺" },
  shopping:      { label: "Shopping",       color: "#F4D8B8", icon: "🛍️" },
  utilities:     { label: "Utilities",      color: "var(--text-primary)", icon: "⚡" },
  health:        { label: "Health",         color: "#C8E8B8", icon: "💊" },
  other:         { label: "Other",          color: "var(--text-secondary)",    icon: "📌" },
};

const FREQ_MONTHLY = { Monthly: 1, Weekly: 52 / 12, Annual: 1 / 12, Quarterly: 1 / 3 };

const DUE_SOON_DATES = new Set(["Mar 13", "Mar 14", "Mar 15", "Mar 16", "Mar 17", "Mar 18", "Mar 19", "Mar 20"]);

// ─── Mini SVG sparkline ────────────────────────────────────────────────────────
function Sparkline({ data, color = "#D4AF37", width = 64, height = 24 }) {
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = (max - min) || 1;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - 2 - ((v - min) / range) * (height - 4);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  return (
    <svg width={width} height={height} style={{ display: "block", overflow: "visible" }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      {/* End dot */}
      {(() => {
        const last = data[data.length - 1];
        const x = width;
        const y = height - 2 - ((last - min) / range) * (height - 4);
        return <circle cx={x} cy={y} r="2.5" fill={color} />;
      })()}
    </svg>
  );
}

// ─── Trend arrow ──────────────────────────────────────────────────────────────
function TrendBadge({ trend }) {
  const map = {
    stable:     { label: "Stable",    color: "var(--text-secondary)",    icon: "→" },
    increasing: { label: "Rising",    color: "#F4B8B8", icon: "↑" },
    decreasing: { label: "Falling",   color: "#52D68A", icon: "↓" },
    seasonal:   { label: "Seasonal",  color: "#E8C87A", icon: "~" },
  };
  const { label, color, icon } = map[trend] || map.stable;
  return (
    <span style={{
      fontSize: "10px", fontFamily: "'JetBrains Mono', monospace",
      color, background: color + "18",
      borderRadius: "5px", padding: "2px 7px",
      letterSpacing: "0.06em",
    }}>{icon} {label}</span>
  );
}

// ─── Custom chart tooltip ─────────────────────────────────────────────────────
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const isProjected = payload[0]?.payload?.projected;
  return (
    <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-strong)", borderRadius: "10px", padding: "12px 16px", minWidth: "140px" }}>
      <div style={{ fontSize: "11px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", marginBottom: "6px" }}>
        {label}{isProjected ? " (projected)" : ""}
      </div>
      {payload.map((p, i) => p.value != null && (
        <div key={i} style={{ fontSize: "12px", color: p.color || "#bbb", fontFamily: "'JetBrains Mono', monospace", marginBottom: "2px" }}>
          {p.name === "avg" ? "3mo avg" : p.name}: ${p.value.toLocaleString()}
        </div>
      ))}
    </div>
  );
}

// ─── Tab: Transactions ────────────────────────────────────────────────────────
function TransactionsTab() {
  const isMobile = useIsMobile();
  const [search, setSearch] = useState("");
  const filtered = TRANSACTIONS.filter(
    tx => tx.merchant.toLowerCase().includes(search.toLowerCase()) ||
          tx.category.toLowerCase().includes(search.toLowerCase())
  );
  const income  = TRANSACTIONS.filter(t => t.amount > 0).reduce((s, t) => s + t.amount, 0);
  const spent   = TRANSACTIONS.filter(t => t.amount < 0).reduce((s, t) => s + t.amount, 0);

  return (
    <div>
      {/* Search */}
      <div style={{ display: "flex", gap: "12px", marginBottom: "20px", alignItems: "center" }}>
        <div style={{ flex: 1, background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: "10px", padding: "10px 16px", display: "flex", alignItems: "center", gap: "10px" }}>
          <span style={{ color: "var(--text-muted)", fontSize: "14px" }}>⌕</span>
          <input
            placeholder="Search transactions..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, background: "none", border: "none", outline: "none", color: "var(--text-secondary)", fontSize: "13px", fontFamily: "'Capsule Sans', sans-serif" }}
          />
        </div>
      </div>

      <div style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: "16px", padding: "22px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: isMobile ? "flex-start" : "center", flexDirection: isMobile ? "column" : "row", gap: isMobile ? "12px" : "0", marginBottom: "20px" }}>
          <div style={{ fontSize: "11px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", textTransform: "uppercase" }}>
            All Transactions
          </div>
          <div style={{ display: "flex", gap: "20px" }}>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: isMobile ? "15px" : "18px", fontFamily: "'Capsule Sans', sans-serif", fontWeight: "600", color: "#52D68A" }}>+{fmt(income)}</div>
              <div style={{ fontSize: "10px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>INCOME</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: isMobile ? "15px" : "18px", fontFamily: "'Capsule Sans', sans-serif", fontWeight: "600", color: "#F87171" }}>{fmt(spent)}</div>
              <div style={{ fontSize: "10px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>SPENT</div>
            </div>
          </div>
        </div>
        {filtered.map(tx => <TransactionRow key={tx.id} tx={tx} />)}
        {filtered.length === 0 && (
          <div style={{ textAlign: "center", padding: "40px 0", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", fontSize: "13px" }}>No transactions found</div>
        )}
      </div>
    </div>
  );
}

// ─── Tab: Recurring ───────────────────────────────────────────────────────────
function RecurringTab() {
  const isMobile = useIsMobile();
  const [items, setItems] = useState(RECURRING_TRANSACTIONS);

  const toggleActive = (id) => setItems(prev =>
    prev.map(r => r.id === id ? { ...r, active: !r.active } : r)
  );

  const activeItems  = items.filter(r => r.active);
  const monthlyTotal = activeItems.reduce((s, r) => s + r.amount * (FREQ_MONTHLY[r.frequency] || 1), 0);
  const annualTotal  = monthlyTotal * 12;
  const upcomingSoon = activeItems.filter(r => DUE_SOON_DATES.has(r.nextDate)).length;

  // Group by category for summary
  const byCategory = activeItems.reduce((acc, r) => {
    const mo = r.amount * (FREQ_MONTHLY[r.frequency] || 1);
    acc[r.category] = (acc[r.category] || 0) + mo;
    return acc;
  }, {});
  const topCategory = Object.entries(byCategory).sort((a, b) => b[1] - a[1])[0];

  return (
    <div>
      {/* Summary cards */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: "14px", marginBottom: "20px" }}>
        {[
          { label: "Monthly Recurring",   value: fmt(monthlyTotal),        sub: `${activeItems.length} active bills`,  color: "#F4B8B8" },
          { label: "Annual Recurring",    value: fmt(annualTotal),          sub: "Committed per year",                 color: "#E8C87A" },
          { label: "Top Category",        value: topCategory?.[0] || "—",  sub: topCategory ? fmt(topCategory[1]) + "/mo" : "", color: "#D4B8E8" },
          { label: "Due This Week",       value: String(upcomingSoon),      sub: "payments upcoming",                  color: "#52D68A" },
        ].map(s => (
          <div key={s.label} style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: "14px", padding: "18px 20px" }}>
            <div style={{ fontSize: "10px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "8px" }}>{s.label}</div>
            <div style={{ fontSize: "20px", fontFamily: "'Capsule Sans', sans-serif", fontWeight: "700", color: s.color }}>{s.value}</div>
            <div style={{ fontSize: "11px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", marginTop: "4px" }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: "16px", padding: "22px" }}>
        <div style={{ fontSize: "11px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "16px" }}>
          Recurring Payments
        </div>

        {/* Desktop header */}
        {!isMobile && (
          <div style={{
            display: "grid",
            gridTemplateColumns: "2.2fr 1.2fr 0.9fr 0.9fr 1.1fr 0.9fr 1.1fr 1.3fr 0.8fr",
            gap: "10px", padding: "8px 0",
            borderBottom: "1px solid var(--border)", marginBottom: "4px",
          }}>
            {["Merchant", "Category", "Amount", "Frequency", "Monthly Equiv.", "Next Date", "6-mo Trend", "Annual Cost", "Status"].map(h => (
              <div key={h} style={{ fontSize: "9px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.09em", textTransform: "uppercase" }}>{h}</div>
            ))}
          </div>
        )}

        {/* Rows — desktop table / mobile cards */}
        {items.map(r => {
          const moEquiv  = r.amount * (FREQ_MONTHLY[r.frequency] || 1);
          const annual   = moEquiv * 12;
          const isDueSoon = DUE_SOON_DATES.has(r.nextDate);
          const trendColor = r.trend === "increasing" ? "#F4B8B8"
                           : r.trend === "decreasing"  ? "#52D68A"
                           : r.trend === "seasonal"    ? "#E8C87A" : "#666";

          if (isMobile) {
            return (
              <div key={r.id} style={{
                padding: "14px 0", borderBottom: "1px solid var(--border)",
                opacity: r.active ? 1 : 0.45, transition: "opacity 0.2s",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <div style={{ width: "36px", height: "36px", borderRadius: "10px", background: "var(--bg-surface)", border: "1px solid var(--border-strong)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px", flexShrink: 0 }}>
                    {r.icon}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ fontSize: "13px", color: "var(--text-primary)", fontWeight: "500" }}>{r.merchant}</div>
                      <div style={{ fontSize: "14px", fontFamily: "'Capsule Sans', sans-serif", fontWeight: "600", color: "var(--text-primary)" }}>{fmt(r.amount)}</div>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "4px" }}>
                      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                        <span style={{ fontSize: "10px", fontFamily: "'JetBrains Mono', monospace", background: "var(--bg-surface)", color: "var(--text-secondary)", borderRadius: "5px", padding: "1px 6px" }}>{r.frequency}</span>
                        {isDueSoon && r.active && <span style={{ fontSize: "9px", color: "#E8C87A", fontFamily: "'JetBrains Mono', monospace" }}>● DUE {r.nextDate}</span>}
                      </div>
                      <button
                        onClick={() => toggleActive(r.id)}
                        style={{ width: "38px", height: "22px", borderRadius: "11px", background: r.active ? "#52D68A" : "var(--bg-hover)", border: "none", cursor: "pointer", position: "relative", transition: "background 0.2s", flexShrink: 0 }}
                      >
                        <div style={{ position: "absolute", top: "3px", left: r.active ? "19px" : "3px", width: "16px", height: "16px", borderRadius: "50%", background: r.active ? "var(--bg-deepest)" : "var(--text-faint)", transition: "left 0.2s" }} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          }

          return (
            <div
              key={r.id}
              style={{
                display: "grid",
                gridTemplateColumns: "2.2fr 1.2fr 0.9fr 0.9fr 1.1fr 0.9fr 1.1fr 1.3fr 0.8fr",
                gap: "10px", padding: "14px 0",
                borderBottom: "1px solid var(--border)", alignItems: "center",
                opacity: r.active ? 1 : 0.45,
                transition: "opacity 0.2s",
              }}
            >
              {/* Merchant */}
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <div style={{ width: "34px", height: "34px", borderRadius: "10px", background: "var(--bg-surface)", border: "1px solid var(--border-strong)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px", flexShrink: 0 }}>
                  {r.icon}
                </div>
                <div>
                  <div style={{ fontSize: "13px", color: "var(--text-primary)", fontWeight: "500" }}>{r.merchant}</div>
                  {isDueSoon && r.active && (
                    <div style={{ fontSize: "9px", color: "#E8C87A", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.08em", marginTop: "2px" }}>
                      ● DUE SOON
                    </div>
                  )}
                </div>
              </div>

              {/* Category */}
              <div>
                <span style={{
                  fontSize: "10px", fontFamily: "'JetBrains Mono', monospace",
                  background: "var(--bg-surface)", color: "var(--text-secondary)", borderRadius: "5px",
                  padding: "2px 7px", letterSpacing: "0.06em",
                }}>{r.category}</span>
              </div>

              {/* Amount */}
              <div style={{ fontSize: "14px", fontFamily: "'Capsule Sans', sans-serif", fontWeight: "600", color: "var(--text-primary)" }}>
                {fmt(r.amount)}
              </div>

              {/* Frequency */}
              <div style={{ fontSize: "12px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>{r.frequency}</div>

              {/* Monthly equivalent */}
              <div style={{ fontSize: "12px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace" }}>
                {r.frequency === "Monthly" ? "—" : fmt(moEquiv) + "/mo"}
              </div>

              {/* Next date */}
              <div>
                <div style={{ fontSize: "12px", color: isDueSoon ? "#E8C87A" : "#777", fontFamily: "'JetBrains Mono', monospace", fontWeight: isDueSoon ? "600" : "400" }}>
                  {r.nextDate}
                </div>
              </div>

              {/* 6-mo trend sparkline */}
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Sparkline data={r.history} color={trendColor} width={56} height={22} />
                <TrendBadge trend={r.trend} />
              </div>

              {/* Annual cost */}
              <div style={{ fontSize: "13px", fontFamily: "'Capsule Sans', sans-serif", fontWeight: "600", color: "#D4AF37" }}>
                {fmt(annual)}
              </div>

              {/* Toggle */}
              <div>
                <button
                  onClick={() => toggleActive(r.id)}
                  style={{
                    width: "38px", height: "22px", borderRadius: "11px",
                    background: r.active ? "#52D68A" : "var(--bg-hover)",
                    border: "none", cursor: "pointer", position: "relative",
                    transition: "background 0.2s",
                  }}
                >
                  <div style={{
                    position: "absolute", top: "3px",
                    left: r.active ? "19px" : "3px",
                    width: "16px", height: "16px", borderRadius: "50%",
                    background: r.active ? "var(--bg-deepest)" : "var(--text-faint)",
                    transition: "left 0.2s",
                  }} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Tab: Insights (Trends + Projections) ────────────────────────────────────
function InsightsTab() {
  const isMobile = useIsMobile();

  // ── Build chart data: 12 historical + 3 projected ─────────────────────────
  const chartData = useMemo(() => {
    // Rolling 3-month average for each historical point
    const withAvg = MONTHLY_SPENDING_TREND.map((m, i) => {
      const slice = MONTHLY_SPENDING_TREND.slice(Math.max(0, i - 2), i + 1);
      const avg   = Math.round(slice.reduce((s, x) => s + x.total, 0) / slice.length);
      return { ...m, actual: m.total, projected: null, avg };
    });
    const projRows = PROJECTED_MONTHS.map(p => ({
      month: p.month, actual: null, projected: p.base, avg: null,
      low: p.low, high: p.high,
    }));
    return [...withAvg, ...projRows];
  }, []);

  // ── Category stats across 12 months ───────────────────────────────────────
  const catStats = useMemo(() => {
    return Object.keys(CAT_META).map(key => {
      const vals = MONTHLY_SPENDING_TREND.map(m => m[key] || 0);
      const total = vals.reduce((s, v) => s + v, 0);
      const avg   = total / vals.length;
      // 3-month trend vs 12-month avg
      const recent3 = vals.slice(-3).reduce((s, v) => s + v, 0) / 3;
      const pctChg  = ((recent3 - avg) / avg) * 100;
      const trend   = pctChg > 5 ? "increasing" : pctChg < -5 ? "decreasing" : "stable";
      return { key, ...CAT_META[key], total, avg: Math.round(avg), recent3: Math.round(recent3), pctChg, trend, history: vals };
    }).sort((a, b) => b.total - a.total);
  }, []);

  // ── Summary stats ──────────────────────────────────────────────────────────
  const avgMonthly  = Math.round(MONTHLY_SPENDING_TREND.reduce((s, m) => s + m.total, 0) / MONTHLY_SPENDING_TREND.length);
  const peakMonth  = MONTHLY_SPENDING_TREND.reduce((a, b) => b.total > a.total ? b : a);
  const lastMonth  = MONTHLY_SPENDING_TREND[MONTHLY_SPENDING_TREND.length - 1];
  const prevMonth   = MONTHLY_SPENDING_TREND[MONTHLY_SPENDING_TREND.length - 2];
  const momChg      = ((lastMonth.total - prevMonth.total) / prevMonth.total) * 100;

  // YoY: compare last month vs same month prior year
  const yoyPrev = MONTHLY_SPENDING_TREND[MONTHLY_SPENDING_TREND.length - 13] || MONTHLY_SPENDING_TREND[0];
  const yoyChg  = ((lastMonth.total - yoyPrev.total) / yoyPrev.total) * 100;

  return (
    <div>

      {/* ── Summary Cards ─────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: "14px", marginBottom: "22px" }}>
        {[
          { label: "Avg Monthly Spend",  value: fmt(avgMonthly),   sub: "12-month average",             color: "var(--text-primary)" },
          { label: "Month-over-Month",   value: `${momChg >= 0 ? "+" : ""}${momChg.toFixed(1)}%`,    sub: `${lastMonth.month} vs ${prevMonth.month}`, color: momChg <= 0 ? "#52D68A" : "#F4B8B8" },
          { label: "Year-over-Year",     value: `${yoyChg >= 0 ? "+" : ""}${yoyChg.toFixed(1)}%`,    sub: `vs same month last year`,               color: yoyChg <= 0 ? "#52D68A" : "#F4B8B8" },
          { label: "Peak Month",         value: peakMonth.month,   sub: fmt(peakMonth.total),           color: "#E8C87A" },
        ].map(s => (
          <div key={s.label} style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: "14px", padding: "18px 20px" }}>
            <div style={{ fontSize: "10px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "8px" }}>{s.label}</div>
            <div style={{ fontSize: "20px", fontFamily: "'Capsule Sans', sans-serif", fontWeight: "700", color: s.color }}>{s.value}</div>
            <div style={{ fontSize: "11px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", marginTop: "4px" }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* ── Spending Trend Chart ───────────────────────────────────── */}
      <div style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: "16px", padding: "24px", marginBottom: "20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "20px" }}>
          <div>
            <div style={{ fontSize: "11px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", textTransform: "uppercase" }}>Monthly Spending · 12 months + 3-month projection</div>
            <div style={{ fontSize: "11px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", marginTop: "4px" }}>Dotted region = projected range · line = rolling 3-month average</div>
          </div>
          <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <div style={{ width: "10px", height: "10px", borderRadius: "2px", background: "#D4AF37" }} />
              <span style={{ fontSize: "10px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace" }}>Actual</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <div style={{ width: "10px", height: "10px", borderRadius: "2px", background: "#D4AF3744" }} />
              <span style={{ fontSize: "10px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace" }}>Projected</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <div style={{ width: "18px", height: "1.5px", background: "#52D68A", borderTop: "2px dashed #52D68A" }} />
              <span style={{ fontSize: "10px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace" }}>3-mo avg</span>
            </div>
          </div>
        </div>

        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={chartData} barCategoryGap="28%">
            <defs>
              <pattern id="projPattern" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
                <line x1="0" y1="0" x2="0" y2="6" stroke="#D4AF37" strokeWidth="2" opacity="0.3" />
              </pattern>
            </defs>
            <XAxis dataKey="month" tick={{ fill: "var(--text-faint)", fontSize: 9, fontFamily: "'JetBrains Mono', monospace" }} axisLine={false} tickLine={false} interval={0} angle={-30} textAnchor="end" height={36} />
            <YAxis tick={{ fill: "var(--text-faint)", fontSize: 9, fontFamily: "'JetBrains Mono', monospace" }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} width={38} />
            <Tooltip content={<ChartTooltip />} />
            <ReferenceLine y={avgMonthly} stroke="#444" strokeDasharray="3 3" strokeWidth={1} label={{ value: "avg", position: "right", fontSize: 9, fill: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace" }} />

            {/* Actual bars */}
            <Bar dataKey="actual" name="Actual" radius={[3, 3, 0, 0]} maxBarSize={28}>
              {chartData.map((entry, i) => (
                <Cell key={i}
                  fill={entry.month === peakMonth.month ? "#E8C87A" : "#D4AF37"}
                  fillOpacity={entry.actual != null ? 0.82 : 0}
                />
              ))}
            </Bar>

            {/* Projected bars */}
            <Bar dataKey="projected" name="Projected" radius={[3, 3, 0, 0]} maxBarSize={28} fill="#D4AF37" fillOpacity={0.28} />

            {/* Rolling average line */}
            <Line dataKey="avg" name="avg" stroke="#52D68A" strokeWidth={1.5} strokeDasharray="5 3" dot={false} activeDot={{ r: 3 }} connectNulls={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* ── Category Performance + Projections ─────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 380px", gap: "20px" }}>

        {/* Category table */}
        <div style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: "16px", padding: "24px" }}>
          <div style={{ fontSize: "11px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "16px" }}>
            Category Performance · Past 12 Months
          </div>

          {/* Desktop header */}
          {!isMobile && (
            <div style={{ display: "grid", gridTemplateColumns: "1.6fr 0.8fr 0.8fr 0.9fr 1fr 1fr", gap: "12px", padding: "6px 0", borderBottom: "1px solid var(--border)", marginBottom: "8px" }}>
              {["Category", "Monthly Avg", "12-mo Total", "3-mo Avg", "vs Average", "Trend"].map(h => (
                <div key={h} style={{ fontSize: "9px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.09em", textTransform: "uppercase" }}>{h}</div>
              ))}
            </div>
          )}

          {catStats.map(cat => {
            const barW = Math.min(100, (cat.total / catStats[0].total) * 100);
            const delta = cat.recent3 - cat.avg;
            const deltaColor = delta > 0 ? "#F4B8B8" : delta < 0 ? "#52D68A" : "#666";

            if (isMobile) {
              return (
                <div key={cat.key} style={{ padding: "12px 0", borderBottom: "1px solid var(--border)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span style={{ fontSize: "14px" }}>{cat.icon}</span>
                      <span style={{ fontSize: "13px", color: "var(--text-primary)", fontWeight: "500" }}>{cat.label}</span>
                    </div>
                    <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                      <span style={{ fontSize: "12px", fontFamily: "'Capsule Sans', sans-serif", fontWeight: "600", color: "var(--text-primary)" }}>{fmt(cat.total)}</span>
                      <TrendBadge trend={cat.trend} />
                    </div>
                  </div>
                  <div style={{ height: "3px", background: "var(--bg-hover)", borderRadius: "2px" }}>
                    <div style={{ height: "100%", width: `${barW}%`, background: cat.color, borderRadius: "2px", transition: "width 0.8s ease" }} />
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: "4px" }}>
                    <span style={{ fontSize: "10px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>${cat.avg}/mo avg</span>
                    <span style={{ fontSize: "10px", color: deltaColor, fontFamily: "'JetBrains Mono', monospace" }}>{delta >= 0 ? "+" : ""}{fmt(delta)}/mo vs avg</span>
                  </div>
                </div>
              );
            }

            return (
              <div key={cat.key} style={{ display: "grid", gridTemplateColumns: "1.6fr 0.8fr 0.8fr 0.9fr 1fr 1fr", gap: "12px", padding: "13px 0", borderBottom: "1px solid var(--border)", alignItems: "center" }}>
                {/* Category */}
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "5px" }}>
                    <span style={{ fontSize: "14px" }}>{cat.icon}</span>
                    <span style={{ fontSize: "12px", color: "var(--text-primary)", fontWeight: "500" }}>{cat.label}</span>
                  </div>
                  <div style={{ height: "3px", background: "var(--bg-hover)", borderRadius: "2px" }}>
                    <div style={{ height: "100%", width: `${barW}%`, background: cat.color, borderRadius: "2px", transition: "width 0.8s ease" }} />
                  </div>
                </div>

                {/* Monthly avg */}
                <div style={{ fontSize: "12px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace" }}>${cat.avg}</div>

                {/* 12-mo total */}
                <div style={{ fontSize: "12px", fontFamily: "'Capsule Sans', sans-serif", fontWeight: "600", color: "var(--text-primary)" }}>{fmt(cat.total)}</div>

                {/* 3-mo avg */}
                <div style={{ fontSize: "12px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace" }}>${cat.recent3}</div>

                {/* vs average */}
                <div style={{ fontSize: "11px", color: deltaColor, fontFamily: "'JetBrains Mono', monospace" }}>
                  {delta >= 0 ? "+" : ""}{fmt(delta)}/mo
                </div>

                {/* Trend */}
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Sparkline data={cat.history} color={cat.color} width={52} height={20} />
                  <TrendBadge trend={cat.trend} />
                </div>
              </div>
            );
          })}
        </div>

        {/* 3-month projections */}
        <div style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: "16px", padding: "24px" }}>
          <div style={{ fontSize: "11px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "6px" }}>
            Projected Spending
          </div>
          <div style={{ fontSize: "11px", color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace", marginBottom: "20px" }}>
            Based on trailing 3-month average + seasonality
          </div>

          {PROJECTED_MONTHS.map((p, pi) => {
            const rangeW = p.high - p.low;
            const cats   = Object.entries(p.breakdown).sort((a, b) => b[1] - a[1]);
            const topCats = cats.slice(0, 4);
            const restSum = cats.slice(4).reduce((s, [, v]) => s + v, 0);
            return (
              <div key={p.month} style={{
                background: "var(--bg-main)", border: "1px solid var(--border)",
                borderRadius: "12px", padding: "18px 20px",
                marginBottom: pi < PROJECTED_MONTHS.length - 1 ? "12px" : 0,
              }}>
                {/* Month header */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "14px" }}>
                  <div>
                    <div style={{ fontSize: "14px", fontFamily: "'Capsule Sans', sans-serif", fontWeight: "600", color: "var(--text-primary)" }}>{p.month}</div>
                    <div style={{ fontSize: "10px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", marginTop: "2px" }}>
                      Range: {fmt(p.low)} – {fmt(p.high)}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: "20px", fontFamily: "'Capsule Sans', sans-serif", fontWeight: "700", color: "#D4AF37" }}>{fmt(p.base)}</div>
                    <div style={{ fontSize: "10px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", marginTop: "1px" }}>estimated</div>
                  </div>
                </div>

                {/* Confidence bar */}
                <div style={{ position: "relative", marginBottom: "14px" }}>
                  <div style={{ height: "6px", background: "var(--bg-hover)", borderRadius: "3px", position: "relative", overflow: "visible" }}>
                    {/* Range fill */}
                    <div style={{
                      position: "absolute",
                      left: `${((p.low - 2000) / 3000) * 100}%`,
                      width: `${(rangeW / 3000) * 100}%`,
                      height: "100%",
                      background: "linear-gradient(90deg, #D4AF3733, #D4AF3766, #D4AF3733)",
                      borderRadius: "3px",
                    }} />
                    {/* Base marker */}
                    <div style={{
                      position: "absolute",
                      left: `${((p.base - 2000) / 3000) * 100}%`,
                      top: "-3px", width: "3px", height: "12px",
                      background: "#D4AF37", borderRadius: "2px",
                      transform: "translateX(-50%)",
                    }} />
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: "4px" }}>
                    <span style={{ fontSize: "9px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>low {fmt(p.low)}</span>
                    <span style={{ fontSize: "9px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>high {fmt(p.high)}</span>
                  </div>
                </div>

                {/* Top category breakdown */}
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  {topCats.map(([k, v]) => {
                    const meta  = CAT_META[k];
                    const share = ((v / p.base) * 100).toFixed(0);
                    return (
                      <div key={k} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ fontSize: "12px", width: "18px", textAlign: "center" }}>{meta?.icon || "📌"}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ height: "4px", background: "var(--bg-hover)", borderRadius: "2px" }}>
                            <div style={{ height: "100%", width: `${share}%`, background: meta?.color || "#888", borderRadius: "2px" }} />
                          </div>
                        </div>
                        <span style={{ fontSize: "10px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", width: "36px", textAlign: "right" }}>{share}%</span>
                        <span style={{ fontSize: "10px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", width: "52px", textAlign: "right" }}>${v}</span>
                      </div>
                    );
                  })}
                  {restSum > 0 && (
                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "2px" }}>
                      <span style={{ fontSize: "10px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>+ other ${restSum}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function TransactionsPage() {
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState(0);

  return (
    <div>
      {/* Tabs */}
      <div style={{ display: "flex", gap: "4px", marginBottom: "24px", background: "var(--bg-main)", borderRadius: "12px", padding: "4px", border: "1px solid var(--border)", width: isMobile ? "100%" : "fit-content", overflowX: "auto" }}>
        {TABS.map((tab, i) => (
          <button
            key={tab}
            onClick={() => setActiveTab(i)}
            style={{
              padding: "9px 22px",
              background: activeTab === i ? "var(--bg-elevated)" : "transparent",
              border: activeTab === i ? "1px solid var(--border-strong)" : "1px solid transparent",
              borderRadius: "9px",
              fontSize: "13px", fontFamily: "'Capsule Sans', sans-serif",
              color: activeTab === i ? "#D4AF37" : "var(--text-faint)",
              cursor: "pointer", transition: "all 0.18s",
              fontWeight: activeTab === i ? "500" : "400",
            }}
          >
            {tab}
            {tab === "Recurring" && (
              <span style={{ marginLeft: "7px", fontSize: "10px", background: "#F4B8B822", color: "#F4B8B8", borderRadius: "5px", padding: "1px 6px", fontFamily: "'JetBrains Mono', monospace" }}>
                {RECURRING_TRANSACTIONS.filter(r => r.active).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {activeTab === 0 && <TransactionsTab />}
      {activeTab === 1 && <RecurringTab />}
      {activeTab === 2 && <InsightsTab />}
    </div>
  );
}
